import { TTrackTypes } from "@/types";
import { base64Decode, base64Encode } from "./utils";
import { readCartLineAttribute } from "./postcodeDiscount";

/**
 * Large-group pricing: when LARGE_GROUP_MIN_SEATS or more seats are picked on the
 * seats screen, every session of that booking costs LARGE_GROUP_SURCHARGE more per
 * seat. The flag is written once at add-to-cart time, so later quantity changes in the
 * cart don't add or remove it.
 *
 * The checkout price is set by the `seats-left-with-price` cart transform in
 * hk-cart-checkout-functions, and the dashboard breakdown in hyperkarting-next-app reads
 * the same attribute - keep the amount, attribute and limits in sync across all three.
 */
export const LARGE_GROUP_MIN_SEATS = 8;
export const LARGE_GROUP_SURCHARGE = 5;
export const LARGE_GROUP_SURCHARGE_ATTRIBUTE = "_large_group_surcharge";

const LARGE_GROUP_SURCHARGE_FLAG = "true";

export const isLargeGroupBooking = (seats: number): boolean => Number.isFinite(seats) && seats >= LARGE_GROUP_MIN_SEATS;

/** Encoded value for LARGE_GROUP_SURCHARGE_ATTRIBUTE, or null when the booking isn't a large group. */
export const getLargeGroupSurchargeForCart = (seats: number): string | null => (isLargeGroupBooking(seats) ? base64Encode(LARGE_GROUP_SURCHARGE_FLAG) : null);

/** Whether a cart line was added as a large group. Used when lines are rebuilt from the cart. */
export const hasLargeGroupSurchargeOnCartLine = (cartLine: unknown): boolean => {
  const encoded = readCartLineAttribute(cartLine, LARGE_GROUP_SURCHARGE_ATTRIBUTE);
  if (!encoded) return false;

  try {
    return base64Decode(encoded) === LARGE_GROUP_SURCHARGE_FLAG;
  } catch {
    return false;
  }
};

type TPriceLimits = { min: number; max: number };

// The per-seat range the cart transform clamps every session to (getMinimumPrice /
// getMaximumPrice in seats-left-with-price). Unlisted tracks fall back to Main.
const CHECKOUT_PRICE_LIMITS: Partial<Record<TTrackTypes, TPriceLimits>> = {
  MAIN_TRACK: { min: 19, max: 59 },
  INTERMEDIATE_TRACK: { min: 14, max: 39 },
  JUNIOR_TRACK: { min: 14, max: 39 },
  MINI_TRACK: { min: 14, max: 29 },
  BATTLE_KART_TRACK: { min: 19, max: 49 },
};

const DEFAULT_PRICE_LIMITS = CHECKOUT_PRICE_LIMITS.MAIN_TRACK as TPriceLimits;

/**
 * Per-seat session price the checkout will charge before discount functions run
 * (member pricing, race discounts). Mirrors the cart transform:
 * clamp(price + large-group surcharge - postcode discount, track min, track max).
 */
export function getSessionUnitPrice({
  basePrice,
  postcodeDiscount = 0,
  isLargeGroup = false,
  track,
}: {
  basePrice: number;
  postcodeDiscount?: number;
  isLargeGroup?: boolean;
  track?: TTrackTypes | null;
}): number {
  const { min, max } = (track && CHECKOUT_PRICE_LIMITS[track]) || DEFAULT_PRICE_LIMITS;
  const price = basePrice + (isLargeGroup ? LARGE_GROUP_SURCHARGE : 0) - postcodeDiscount;

  return Number(Math.min(max, Math.max(min, price)).toFixed(2));
}
